import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CheckCircle2, XCircle, AlertTriangle, ShieldCheck, Building2, Award, ArrowUpRight, Clock, KeyRound, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const STATUS_THEME = {
  active:    { icon: CheckCircle2, label: "ACTIVE — Valid Credential", text: "text-ec-emerald", bg: "bg-ec-emerald/10", border: "border-ec-emerald/40" },
  expired:   { icon: Clock,        label: "EXPIRED — Not Valid",       text: "text-orange-700", bg: "bg-orange-50",     border: "border-orange-400" },
  suspended: { icon: AlertTriangle,label: "SUSPENDED — Under Review",  text: "text-amber-700",  bg: "bg-amber-50",      border: "border-amber-400" },
  revoked:   { icon: XCircle,      label: "REVOKED — Invalid",         text: "text-ec-error",   bg: "bg-red-50",        border: "border-red-400" },
  pending_review: { icon: Clock,   label: "PENDING REVIEW",            text: "text-zinc-700",   bg: "bg-zinc-100",      border: "border-zinc-300" },
};

export default function VerifyInstitutionAward() {
  const { awardNo } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/awards/verify/${awardNo}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || "Award not found."));
  }, [awardNo]);

  if (err) {
    return (
      <div className="bg-ec-bg min-h-screen" data-testid="verify-error-page">
        <Navbar />
        <section className="max-w-3xl mx-auto px-6 py-24 text-center">
          <XCircle className="w-16 h-16 text-ec-error mx-auto mb-4" />
          <h1 className="font-display text-4xl font-black tracking-tight">This credential could not be verified.</h1>
          <p className="text-ec-ink-soft mt-3 max-w-md mx-auto">
            The award number <span className="font-mono-plex font-bold text-ec-ink">{awardNo}</span> does not exist in the EthicCred registry, or has been removed.
          </p>
          <p className="text-xs text-ec-ink-soft mt-4">If you received this badge externally, treat it as <strong>fabricated</strong> until verified directly with the institution.</p>
          <Link to="/" className="inline-block mt-8 underline text-ec-ink">← Back to EthicCred</Link>
        </section>
        <Footer />
      </div>
    );
  }
  if (!data) return <div className="bg-ec-bg min-h-screen"><Navbar /><div className="max-w-3xl mx-auto px-6 py-24 text-center text-ec-ink-soft">Verifying credential…</div><Footer /></div>;

  const theme = STATUS_THEME[data.status] || STATUS_THEME.expired;
  const Icon = theme.icon;
  const inst = data.institution || {};

  return (
    <div className="bg-ec-bg min-h-screen" data-testid="verify-award-page">
      <Navbar />
      <section className="max-w-3xl mx-auto px-6 py-16">
        <div className="inline-flex items-center gap-2 border border-ec-line bg-white px-3 py-1 text-xs eyebrow">
          <ShieldCheck className="w-3 h-3 text-ec-emerald" /> EthicCred Credential Registry
        </div>

        <div className={`border-2 ${theme.border} ${theme.bg} p-6 mt-6 flex items-center gap-4`} data-testid="verify-status-banner">
          <Icon className={`w-10 h-10 ${theme.text}`} />
          <div>
            <div className={`font-display text-xl font-black ${theme.text}`}>{theme.label}</div>
            <div className="text-xs font-mono-plex text-ec-ink-soft mt-0.5">Award #{data.award_no}</div>
          </div>
        </div>

        <div className="bg-white border border-ec-ink/10 mt-6 p-8 noise-overlay">
          <div className="flex items-center gap-4 mb-6">
            {inst.avatar_url ? (
              <img src={inst.avatar_url} alt="logo" className="w-16 h-16 border border-ec-line rounded-sm object-cover" />
            ) : (
              <div className="w-16 h-16 bg-ec-ink text-white flex items-center justify-center"><Building2 className="w-7 h-7" /></div>
            )}
            <div>
              <div className="eyebrow text-ec-emerald">{data.award_label}</div>
              <h2 className="font-display text-3xl font-black tracking-tight">{inst.organization_name || "Institution"}</h2>
              <div className="font-mono-plex text-xs text-ec-ink-soft mt-1">{inst.ethic_id} · {inst.country || "—"}</div>
            </div>
          </div>

          <dl className="grid sm:grid-cols-2 gap-y-3 gap-x-8 border-t border-ec-line pt-5" data-testid="verify-detail-grid">
            <DL label="Award number" value={<span className="font-mono-plex">{data.award_no}</span>} />
            <DL label="Award type" value={data.award_label} />
            <DL label="Issue date" value={(data.issued_at || "—").slice(0, 10)} />
            <DL label="Expiry date" value={(data.expires_at || "—").slice(0, 10)} />
            <DL label="Verification status" value={<span className={`font-bold ${theme.text}`}>{data.status.toUpperCase().replace("_", " ")}</span>} />
            <DL label="Signature" value={<span className="font-mono-plex text-[10px] break-all">{data.signature}</span>} />
          </dl>

          <QRBlock awardNo={data.award_no} />

          {inst.ethic_id && (
            <Link to={`/institutions/${inst.ethic_id}/awards`} className="mt-6 inline-flex items-center gap-1 text-sm text-ec-emerald hover:underline" data-testid="verify-view-institution">
              View full institution award history <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>

        <p className="text-xs text-ec-ink-soft mt-6 max-w-xl text-center mx-auto font-mono-plex" data-testid="verify-footer-note">
          <KeyRound className="w-3 h-3 inline mr-1" />
          This credential is verified live against the EthicCred registry. Institutions earn the right to display badges only while maintaining standards.
        </p>
      </section>
      <Footer />
    </div>
  );
}

function DL({ label, value }) {
  return (
    <div>
      <dt className="eyebrow text-ec-ink-soft text-[10px]">{label}</dt>
      <dd className="text-sm text-ec-ink font-semibold mt-1">{value}</dd>
    </div>
  );
}

function QRBlock({ awardNo }) {
  const verifyUrl = `${window.location.origin}/verify/institution/${awardNo}`;
  const copy = () => {
    navigator.clipboard.writeText(verifyUrl);
    toast.success("Verification URL copied");
  };
  return (
    <div className="border-t border-ec-line mt-6 pt-6 flex flex-wrap items-center gap-6" data-testid="verify-qr-block">
      <div className="bg-white p-3 border border-ec-line rounded-sm">
        <QRCodeSVG
          value={verifyUrl}
          size={140}
          level="H"
          imageSettings={{ src: "", height: 0, width: 0, excavate: false }}
          data-testid="verify-qr-svg"
        />
      </div>
      <div className="flex-1 min-w-[200px]">
        <div className="eyebrow text-ec-emerald mb-1">Scan to verify</div>
        <p className="text-sm text-ec-ink-soft leading-relaxed">
          Scanning this QR opens the live verification page for this credential.
          Treat anything that doesn't match the EthicCred registry as fabricated.
        </p>
        <div className="mt-3 flex items-center gap-2 text-xs font-mono-plex break-all">
          <span className="text-ec-ink-soft">{verifyUrl}</span>
        </div>
        <Button size="sm" variant="outline" onClick={copy} className="mt-3 rounded-sm h-8 gap-1.5" data-testid="verify-copy-url">
          <Copy className="w-3 h-3" /> Copy verify URL
        </Button>
      </div>
    </div>
  );
}
